import { assertNever } from 'owa-assert';
import {
    OrderByValue,
    ExpandValue,
    GetTasksRequestParams,
    TaskStatusFilter,
} from '../schema/request/GetTasksRequestParams';
import {
    isGetDueDateTasksRequestParams,
    isGetMyDayTasksRequestParams,
    isGetFolderTasksRequestParams,
} from './typeGuard';
import { getISODateString } from 'owa-datetime';

/**
 * Build a request url from GetTasksRequestParams.
 * For more information, see the Todo Api Documentation: https://alexanderplatz.visualstudio.com/To-Do/_git/to-do-api-documentation?anchor=Conclusion
 */
export function buildGetTasksUrl(requestParams: GetTasksRequestParams): string {
    const { filters, orderBy, expand } = requestParams;
    const resourcePath = getResourcePath(requestParams);
    const orderByV = getOrderBy(orderBy);
    const expandValue = getExpand([expand]);
    const statusFilter =
        filters.taskStatusFilters.length > 0
            ? `&$filter=${filters.taskStatusFilters.map(getStatusFilter).join(' or ')}`
            : '';
    const customQueryOptions = getCustomQueryOptions(requestParams);
    return `/ows/todo/api/v1/${resourcePath}/tasks/?${customQueryOptions}${statusFilter}&$orderby=${orderByV}&$expand=${expandValue}`;
}

function getExpand(expandValues: ExpandValue[]) {
    return expandValues.join(',');
}

function getOrderBy(order: OrderByValue): string {
    switch (order) {
        case OrderByValue.CompletedDateTime:
            return 'CompletedDateTime/DateTime desc';
        case OrderByValue.OrderDateTime:
            return 'OrderDateTime/DateTime desc';
        case OrderByValue.CommittedOrder:
            return 'OrderDateTime/DateTime desc';
        default:
            assertNever(order);
    }
}

function getStatusFilter(statusFilter: TaskStatusFilter): string {
    return `Status ${statusFilter.equalityOperator} '${statusFilter.status}'`;
}

function getResourcePath(requestParams: GetTasksRequestParams) {
    if (isGetMyDayTasksRequestParams(requestParams)) {
        return 'smartlists/myday';
    } else if (isGetDueDateTasksRequestParams(requestParams)) {
        return 'smartlists/duedate';
    } else if (isGetFolderTasksRequestParams(requestParams)) {
        const { folderId } = requestParams;
        return `taskfolders/${folderId ? folderId : 'tasks'}`;
    }
    return assertNever(requestParams);
}

function getCustomQueryOptions(requestParams: GetTasksRequestParams): string {
    let queryOptionsString = '';

    // add the custom options shared by all request types
    const {
        customQueryOptions: { maxPageSize },
    } = requestParams;
    if (maxPageSize) {
        queryOptionsString += `&maxpagesize=${maxPageSize}`;
    }

    // add the request type specific custom options
    if (isGetMyDayTasksRequestParams(requestParams)) {
        const {
            customQueryOptions: { date },
        } = requestParams;
        const dateFilter = getISODateString(date);
        return queryOptionsString + `&date=${dateFilter}`;
    } else if (isGetDueDateTasksRequestParams(requestParams)) {
        const {
            customQueryOptions: { startDate, endDate },
        } = requestParams;
        const startDateFilter = getISODateString(startDate);
        const endDateFilter = getISODateString(endDate);

        return queryOptionsString + `&startDate=${startDateFilter}&endDate=${endDateFilter}`;
    } else if (isGetFolderTasksRequestParams(requestParams)) {
        return queryOptionsString;
    }
    return assertNever(requestParams);
}
